import { Component, OnInit, Input, ChangeDetectorRef, Output, EventEmitter, ElementRef, HostListener, OnDestroy, NgZone } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations' 
import { DirectionsService } from 'src/app/services/directions.service';
import { Router } from '@angular/router';
import { utf8_encode, utf8_decode } from 'locutus/php/xml';

/**
 * Conté la informació necessària per mostrar una ruta calculada pel servei Inrix
 */
interface InrixData {
  /**
   * Punts per dibuixar la ruta
   */
  routePoints: google.maps.LatLng[],

  /**
   * Duració de la ruta
   */
  duration: string,

  /**
   * Distància total de la ruta
   */
  distance: string,

  /**
   * Consum de combustible de la ruta
   */
  fuel: string,

  /**
   * Resum de la ruta
   */
  summary: string,
  
}

/**
 * Conté informació resumida i detallada sobre les rutes calculades
 */
@Component({
  selector: 'app-llista-rutes',
  templateUrl: './llista-rutes.component.html',
  styleUrls: ['./llista-rutes.component.css']
})
export class LlistaRutesComponent implements OnInit {

  /**
   * Constructor del component
   * @param {DirectionsService} directionsService Servei de rutes de l'aplicació
   * @param {ChangeDetectorRef} cdRef Detecta els canvis del html
   * @param {ElementRef} elementRef Serveix per mantenir referència a elements de la plantilla del component
   * @param {Router} router Instància de router de l'aplicació
   * @param {NgZone} ngZone 
   */
  constructor(public  directionsService: DirectionsService,public cdRef:ChangeDetectorRef, private elementRef: ElementRef, private router:Router,
    private ngZone: NgZone) { }

  /**
   * Emet un valor quan l'usuari vol esborrar la informació sobre rutes 
   */
  @Output() deleteEmitter = new EventEmitter<boolean>();

  /**
   * Preu per kilòmetre de la ruta
   */
  @Input() preuKm: number;

  /**
   * @ignore
   */
  @HostListener('window:resize')
  onResize() {
    this.setScreenSize();
  };

  /**
   * Conté les rutes calculades pel servei de Google Maps Directions
   */
  routes: google.maps.DirectionsRoute[];

  /**
   * Indica si s'ha trobat o no una ruta
   */
  routeNotFoundFlag: boolean;

  /**
   * Indica si l'usuari vol mostrar o no els detalls d'una ruta
   */
  routeDetails: boolean = false;

  /**
   * Índex de la ruta de Google Maps Directiosn seleccionada
   */
  selectedRouteIndex: number = 0;

  /**
   * Finestra d'informació per mostrar les indicacions al mapa
   */
  infowindow: google.maps.InfoWindow;

  /**
   * @ignore
   */
  urlImg: string = "assets/img/";

  /**
   * @ignore
   */
  iconNames = ["routeIconA.svg","routeIconB.svg","routeIconC.svg","routeIconD.svg","routeIconE.svg","routeIconF.svg","routeIconG.svg","routeIconH.svg"]
  
  /**
   * Ruta de Google Maps Directions seleccionada
   */
  selectedRoute: google.maps.DirectionsRoute;

  /**
   * Dades provinents del servei d'Inrix
   */
  inrixData: InrixData;

  /**
   * Indica si s'ha fet servir o no el servei de rutes d'Inrix
   */
  inrixFlag:boolean = false;

  /**
   * Indica l'altura de la pantalla
   */
  screenHeight: number = 100; 

  /**
   * Indica l'amplada de la pantalla
   */
  screenWidth: number = 100; 


  /**
   * Actualitza els valors de l'alçada i amplada de la pantalla
   */
  setScreenSize(): void{
    this.screenHeight = window.innerHeight;
    this.elementRef.nativeElement.style.setProperty('--height', this.screenHeight + 'px');
    this.screenWidth = window.innerWidth;
    this.elementRef.nativeElement.style.setProperty('--width', this.screenWidth + 'px');
    
  }

  /**
   * Donada una ruta, calcula la distància total que s'ha de recòrrer
   * @param {google.maps.DirectionsRoute} route ruta de Google Maps Directions 
   * @returns {string} Text amb la distància 
   */
  getDistance(route: google.maps.DirectionsRoute): string {
    return this.directionsService.calcDistance(route);
  }
  
  /**
   * Donada una ruta, calcula la duració total per recòrrer-la
   * @param {google.maps.DirectionsRoute} route ruta de Google Maps Directions 
   * @returns {string} Text amb la duració
   */
  getDuration(route: google.maps.DirectionsRoute): string {
    return this.directionsService.calcDuration(route);
  }

  /**
   * Donada una ruta, calcula el consum en € de combustible 
   * @param {google.maps.DirectionsRoute} route ruta de Google Maps Directions
   * @returns {string} Text amb el consum
   */
  getFuel(route: google.maps.DirectionsRoute): number {
    return this.directionsService.calcFuel(route);
  }


  /**
   * Canvia la URL de la pàgina per imprimir 
   */
  navigateToPrint(): void{
    this.ngZone.run(() => {
      //this.cdRef.detectChanges();
      this.router.navigateByUrl('/print');
    })
    
  }
  
  /**
   * Centra el mapa a l'step passat per paràmetre i mostra les instruccions en una finestra d'informació
   * @param step l'step que s'ha seleccionat
   */
  selectStep(step: google.maps.DirectionsStep): void{
    var map = this.directionsService.getMap();
    map.panTo(step.start_location);
    var instructionsHTML = "<div style='padding:40px; font-size: 14px'>" + step.instructions + "</div>"
    map.setZoom(15);
    this.infowindow.close();
    this.infowindow = new google.maps.InfoWindow({
      position: step.start_location,
      content: instructionsHTML
    });
  }

  /**
   * Selecciona la ruta a mostrar
   * @param {google.maps.DirectionsRoute} route Canvia la ruta seleccionada al mapa
   */
  selectRoute(route: google.maps.DirectionsRoute): void{
    this.selectedRoute = route;

    this.cdRef.detectChanges();
  }

  /**
   * Fa la conversió a utf8 per canviar valors de caràcters no imprimibles
   * @param utfString text sense codificar
   * @returns {string} Text codificat
   */
  convertString(utfString: string): string{
    var encoded: string  = utf8_decode(utfString);
    encoded = encoded.replace('Ü','Ó');
    
    return encoded;
  }


  /**
   * Inicialitza les propietats del component i es subscriu als diferents serveis de rutes de l'aplicació
   */
  ngOnInit() {
    this.setScreenSize();
    this.infowindow = new google.maps.InfoWindow();
    this.directionsService.setPanel(document.getElementById("route-steps-container"));
    this.directionsService.getRoutes().subscribe(data => {
      this.routes = data;
      this.selectedRoute = data[0];
      if(data.length === 0){
        this.routeDetails = false;
        this.infowindow.close();
      }
      this.cdRef.detectChanges();
    });

    this.directionsService.getRouteNotFoundFlag().subscribe(data => {
      this.routeNotFoundFlag = data;
    })

    this.directionsService.getInrix().subscribe(data => {
      this.inrixData = data;
      if (!this.cdRef['destroyed']) {
       this.cdRef.detectChanges();
      }
    });

    this.directionsService.getInrixFlag().subscribe( value => {
      this.inrixFlag = value;
    })
    
  }

  /**
   * @ignore
   */
  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.elementRef.nativeElement.remove();
  }


}